import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { insightsApi, insightCategoryLabel, type ClientInterview } from '../api/insights'
import { useAuth } from '../contexts/AuthContext'
import { Spinner } from './Spinner'

const fmtWhen = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
const todayISO = () => new Date().toISOString().slice(0, 10)

function convLabel(c: ClientInterview): string {
  return c.mode === 'interview' ? 'Entrevista' : c.reportId ? 'Conversa sobre o relatório' : 'Conversa'
}

/**
 * HISTÓRICO DE ENTREVISTAS (aba "Entrevistas" do cliente) — cada entrevista do dossiê, de
 * qualquer gerente, e as conversas que gravaram fato, da mais recente para a mais antiga:
 * quem fez, quantas respostas deu e o que a IA salvou. Os fatos aparecem como estão hoje
 * (editados, desativados ou rejeitados depois); fato excluído some daqui também.
 */
export function InterviewHistory({ clientId }: { clientId: string }) {
  const { user } = useAuth()
  const { data, isLoading } = useQuery({
    queryKey: ['client-interviews', clientId],
    queryFn: () => insightsApi.interviews(clientId),
  })
  const list = data?.interviews ?? []

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-xs text-slate-500">
          Cada entrevista do dossiê (e cada conversa que gravou algo), da mais recente para a mais antiga: quem fez,
          quantas respostas deu e o que a IA salvou no dossiê. Os fatos aparecem como estão hoje.
        </p>
        <Link
          to={`/clients/chat?client=${clientId}&mode=interview`}
          className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
        >
          🎤 Nova entrevista (10 min)
        </Link>
      </div>

      {isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
          Nenhuma entrevista feita com este cliente ainda.
        </p>
      ) : (
        <div className="space-y-2">
          {/* Abertas as que salvaram fato; as sem fato ficam numa linha (o resumo já diz tudo). */}
          {list.map((c) => (
            <details key={c.id} open={c.facts.length > 0} className="rounded-lg border border-slate-200 bg-white">
              <summary className="flex cursor-pointer select-none flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{convLabel(c)}</span>
                <span>· {fmtWhen(c.createdAt)}</span>
                <span>· {c.user.name}</span>
                <span className="text-slate-500">
                  · {c.answers} resposta{c.answers === 1 ? '' : 's'}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    c.facts.length > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {c.facts.length > 0
                    ? `${c.facts.length} fato${c.facts.length === 1 ? '' : 's'} salvo${c.facts.length === 1 ? '' : 's'}`
                    : 'nenhum fato salvo'}
                </span>
              </summary>
              <div className="border-t border-slate-100 px-3 py-2">
                {c.facts.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {c.answers === 0
                      ? 'A IA fez a primeira pergunta e ninguém respondeu.'
                      : 'As respostas não viraram fato — a IA só salva o que é durável sobre o cliente.'}
                  </p>
                ) : (
                  <ol className="list-decimal space-y-1.5 pl-5">
                    {c.facts.map((f) => {
                      const tag =
                        f.status === 'rejected'
                          ? 'rejeitado depois'
                          : !f.active
                            ? 'desativado'
                            : f.validUntil && f.validUntil.slice(0, 10) < todayISO()
                              ? 'vencido'
                              : null
                      return (
                        <li key={f.id} className={`text-sm text-slate-800 ${tag ? 'opacity-60' : ''}`}>
                          {f.content}
                          <span className="ml-2 whitespace-nowrap text-[11px] text-slate-400">
                            {insightCategoryLabel(f.category)}
                            {f.weight === 'absolute' ? ' · verdade absoluta' : ''}
                            {tag ? ` · ${tag}` : ''}
                          </span>
                        </li>
                      )
                    })}
                  </ol>
                )}
                {/* A conversa é de quem a fez (as mensagens não aparecem para os outros). */}
                {c.userId === user?.id && (
                  <Link
                    to={`/clients/chat?client=${clientId}&conversation=${c.id}`}
                    className="mt-2 inline-block text-xs font-medium text-brand-600 hover:underline"
                  >
                    Abrir a conversa →
                  </Link>
                )}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  )
}
